import React from "react";
import { NavLink } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import ContactCTA from "../components/ContactCTA";
import FaqSection from "../components/FaqSection";
import WhyCloudare from "../components/WhyCloudare";
import { PhosphorIcon } from "../components/PhosphorIcon";
import useReveal from "../hooks/useReveal";
import "../styles/tax-page.css";
import "../styles/erp-integration.css";

const STEPS = [
  { n: 1, title: "Extract", body: "Read invoice data from your ERP — Oracle, SAP, Zoho, Dynamics or Odoo — via API, IDoc, OIC or file." },
  { n: 2, title: "Map & validate", body: "Map to the authority's schema (UBL 2.1, PINT AE, GST INV-01, Factur-X) and validate before submission." },
  { n: 3, title: "Transmit", body: "Send to the IRP / ASP / PDP / PEPPOL access point — clearance or reporting per the mandate." },
  { n: 4, title: "Return & archive", body: "Write IRN, QR, stamp and status back into the ERP, with compliant tamper-evident archival." },
];

const FEATURES = [
  { icon: "Database", title: "Data stays in your ERP", body: "Invoices, IRNs and statuses live in your system of record — not in a third-party silo you can't audit." },
  { icon: "ArrowsLeftRight", title: "ASP-neutral", body: "Swap the e-invoicing provider without rebuilding the integration. The bridge is ours; the choice is yours." },
  { icon: "ShieldCheck", title: "Validation first", body: "Pre-submission checks stop rejections at the authority and protect your input tax credit." },
  { icon: "Globe", title: "Multi-country", body: "One integration approach spanning India GST, KSA ZATCA, UAE PINT AE, Malaysia LHDN and EU PEPPOL." },
];

const FAQS = [
  { question: "How does e-invoicing integrate with my ERP?", answer: "Cloudare builds a layer that extracts invoice data from your ERP, maps and validates it to the tax authority's required format, transmits it to the IRP/ASP/PDP, and writes the response (IRN, QR, stamp, status) back into the ERP. Your data stays in your system of record." },
  { question: "Which ERPs do you integrate with e-invoicing?", answer: "SAP (S/4HANA and ECC), Oracle Fusion and EBS, Microsoft Dynamics 365, NetSuite, Odoo, Zoho, Tally and Sage — plus other systems through a documented API." },
  { question: "I don't have an enterprise ERP — can you still help?", answer: "Yes. You don't need a large enterprise ERP to become compliant. We work with SMB and mid-market systems like Zoho, Odoo, Tally and QuickBooks, and even with spreadsheets or a billing system, as long as invoice data can be exported or accessed via API. We assess what you have, recommend the simplest compliant path, and build the connection — often a lightweight adapter rather than a heavy platform." },
  { question: "What if my integration needs middleware?", answer: "We handle both ends of this. Where you already run enterprise middleware — Oracle Integration Cloud (OIC), Dell Boomi, MuleSoft or a client-specific platform — we build the e-invoicing flows on it. Where no middleware exists, we deploy a custom Python adapter that sits between your ERP and the ASP/IRP/PDP, giving you a clean, maintainable integration without forcing you to buy a middleware suite." },
  { question: "Do I need Oracle OIC for the integration?", answer: "Not necessarily. On Oracle we can use OIC if you run it, or a focused middleware service / custom adapter if OIC scope or licensing doesn't justify it. We pick the upgrade-safe option for your estate." },
  { question: "Can the same integration handle multiple countries?", answer: "Yes. The extract-map-validate-transmit pattern is consistent; the country profile (format, authority, rules) is configuration on top. This is why multi-country rollouts reuse most of the work." },
];

export default function ErpIntegrationPage() {
  useReveal();
  return (
    <React.Fragment>
      <Seo
        path="/einvoicing-erp-integration"
        title="E-Invoicing Integration With Your ERP — Oracle, SAP, Zoho | Cloudare"
        description="Integrate e-invoicing compliance directly into Oracle, SAP, Zoho, Dynamics or Odoo. ASP-neutral, data stays in your ERP, multi-country: GST, ZATCA, PINT AE, LHDN, PEPPOL."
      />
      <Navbar />

      <section className="tax-hero reveal">
        <div className="tax-hero__inner">
          <p className="tax-hero__eyebrow">E-invoicing × ERP</p>
          <h1 className="tax-hero__title">E-invoicing, built into your ERP</h1>
          <p className="tax-hero__subhead">
            Not another portal to log into. Cloudare wires global e-invoicing
            compliance directly into Oracle, SAP, Zoho, Dynamics or Odoo — so
            your data stays in your system of record and your team keeps working
            the way they already do.
          </p>
          <div className="tax-hero__cta">
            <a className="btn-primary" href="https://calendly.com/cloudare" target="_blank" rel="noopener noreferrer">
              Book a discovery call
            </a>
            <NavLink className="btn-secondary" to="/contact-us">Contact us</NavLink>
          </div>
        </div>
      </section>

      {/* How it works stepper */}
      <section className="ei-section">
        <header className="why-cloudare__header reveal">
          <p className="why-cloudare__eyebrow">How it works</p>
          <h2 className="why-cloudare__title">Four steps, inside your ERP</h2>
        </header>
        <div className="ei-flow reveal-stagger">
          {STEPS.map((s) => (
            <div className="ei-step" key={s.n}>
              <span className="ei-step__num">{s.n}</span>
              <h3>{s.title}</h3>
              <p>{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      <WhyCloudare
        title="Why integrate, instead of bolt-on"
        lede="A portal-only approach fragments your data and locks you to one vendor. Integration keeps both in your control."
        features={FEATURES}
        elevated
      />

      {/* ERP shortcuts */}
      <section className="ei-section">
        <header className="why-cloudare__header reveal">
          <p className="why-cloudare__eyebrow">By platform</p>
          <h2 className="why-cloudare__title">Pick your ERP</h2>
        </header>
        <div className="ei-erplinks reveal-stagger">
          <NavLink to="/erp/fusion" className="ei-erplink">Oracle Fusion</NavLink>
          <NavLink to="/erp/ebs" className="ei-erplink">Oracle EBS</NavLink>
          <NavLink to="/erp/sap" className="ei-erplink">SAP</NavLink>
          <NavLink to="/erp/dynamics" className="ei-erplink">MS Dynamics</NavLink>
          <NavLink to="/erp/odoo" className="ei-erplink">Odoo</NavLink>
          <NavLink to="/services/zoho" className="ei-erplink">Zoho</NavLink>
        </div>
        <p className="ei-note reveal">
          Tracking mandates across 120+ countries?{" "}
          <a href="https://einvoice.global" target="_blank" rel="noopener noreferrer">
            Explore eInvoice.Global
          </a>{" "}
          — our free compliance tracker.
        </p>
      </section>

      <div id="faq">
        <FaqSection
          eyebrow="Frequently asked questions"
          title="E-invoicing ERP integration — FAQ"
          faqs={FAQS}
          emitJsonLd
        />
      </div>

      <ContactCTA
        title="Integrate e-invoicing into your ERP"
        subtitle="Tell us your ERP, versions and target mandates — we'll scope the integration end to end."
      />
      <Footer />
    </React.Fragment>
  );
}
